import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Switch,
  Box,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from 'src/services/BaseService';
import Services from 'src/services/Services';
import { SERVICES, SERVICE_LABELS, parseActiveServices } from 'src/utils/servicePermissions';

const EditAvmPermissionsModal = ({ open, onClose, mallData, onSave }) => {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullMallData, setFullMallData] = useState(null);

  // Fetch full mall data from API when modal opens
  useEffect(() => {
    if (open && mallData && mallData.id) {
      setLoading(true);

      Services.GetMallInformation(parsedUser.token, mallData.id)
        .then((response) => {
          if (response.data && response.data.result) {
            const fullData = response.data.result;
            setFullMallData(fullData);

            // Initialize form data
            const initialData = {};
            Object.values(SERVICES).forEach(service => {
              initialData[service] = false;
            });

            // Set active services to true
            if (fullData.activeServices) {
              const activeServices = parseActiveServices(fullData.activeServices);

              activeServices.forEach(service => {
                if (Object.values(SERVICES).includes(service)) {
                  initialData[service] = true;
                }
              });
            }

            setFormData(initialData);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error('Error fetching mall data:', error);
          toast.error('AVM bilgileri yüklenirken bir hata oluştu.');
          setLoading(false);
        });
    }
  }, [open, mallData?.id]);

  const handleSwitchChange = (service) => {
    setFormData(prevState => ({
      ...prevState,
      [service]: !prevState[service]
    }));
  };

  const handleSave = async () => {
    if (!fullMallData) {
      toast.error('AVM bilgileri yüklenmedi.');
      return;
    }

    setSaving(true);
    try {
      // Collect active services
      const activeServices = Object.entries(formData)
        .filter(([_, isActive]) => isActive)
        .map(([service, _]) => service);

      // Prepare request body with all mall data, only changing activeServices
      const requestBody = {
        id: fullMallData.id,
        title: fullMallData.title,
        city: fullMallData.city,
        county: fullMallData.county,
        hasMuhaberat: fullMallData.hasMuhaberat,
        transportationToolsWithPrice: fullMallData.transportationToolsWithPrice || [],
        canViewBoxMenu: fullMallData.canViewBoxMenu || "0",
        canViewCheckroomMenu: fullMallData.canViewCheckroomMenu || "0",
        boxList: fullMallData.boxList || [],
        checkroomList: fullMallData.checkroomList || [],
        activeServices: activeServices
      };

      // Send update to backend using the same endpoint as AddEditAvmModal
      const response = await api.post('/flow/malls-update', requestBody);

      if (response.data && response.data.status) {
        toast.success('Yetkilendirmeler başarıyla güncellendi.');
        if (onSave) {
          onSave();
        }
        onClose();
      } else {
        toast.error(response.data?.message || 'Güncelleme başarısız oldu.');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Yetkilendirmeler güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  // Service groups for 2-column layout
  const serviceGroups = Object.values(SERVICES);
  const leftColumn = serviceGroups.slice(0, Math.ceil(serviceGroups.length / 2));
  const rightColumn = serviceGroups.slice(Math.ceil(serviceGroups.length / 2));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mallData?.title} - Yönetim Yetkileri
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} sm={6}>
              {leftColumn.map(service => (
                <Box
                  key={service}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Typography variant="body2">
                    {SERVICE_LABELS[service]}
                  </Typography>
                  <Switch
                    checked={formData[service] || false}
                    onChange={() => handleSwitchChange(service)}
                    color="primary"
                  />
                </Box>
              ))}
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} sm={6}>
              {rightColumn.map(service => (
                <Box
                  key={service}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Typography variant="body2">
                    {SERVICE_LABELS[service]}
                  </Typography>
                  <Switch
                    checked={formData[service] || false}
                    onChange={() => handleSwitchChange(service)}
                    color="primary"
                  />
                </Box>
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAvmPermissionsModal;

